import { z } from 'zod'

import { MAX_TREND_FUNDS } from '@/features/mutual-funds/lib/defaults'
import { codeParamSchema } from '@/schemas/common'
import { pageSizeSchema } from '@/schemas/table'

/**
 * URL building blocks shared by the Mutual funds routes.
 *
 * Each route's own schema lives in its `<Screen>.loader.ts` and is exported
 * from there as the route's `validateSearch` too (docs/route-loading.md,
 * "Search params" — `StockScreen.loader.ts`'s pattern), so the loader and the
 * route can never read one URL two ways. What more than one of those schemas
 * needs is declared once, here.
 *
 * EVERY FIELD FALLS BACK instead of throwing (`.catch`), the rule
 * `PublicIssuesScreen` and `HeatmapScreen` state: these params are filters,
 * tabs and pages over one screen, never a different screen, so a hand-edited
 * or stale value lands on the default view rather than an error page.
 *
 * No `createFileRoute` here, so a test may import this module.
 */

/**
 * A holdings-period label from `/periods/` ("Bhadra 2083"). Absent means the
 * latest period — the backend's own default, so a screen passes `null` and
 * reads the resolved label back off the payload's `period`. A label that
 * matches no filings is NOT rejected here, but the backend no longer answers
 * it with an empty payload: since 2026-10-29 an unknown `period` is a
 * 400 `MUTUAL_FUND_PERIOD_UNKNOWN`. A screen must only send a label it read
 * off `/periods/`: a hand-edited one that is not there should read as the
 * latest period, never be passed on.
 */
export const periodParamSchema = codeParamSchema.optional().catch(undefined)

/**
 * Codes and symbols are uppercased on the way in. The backend matches them
 * case-blind (`?fund=niblsf` answers NIBLSF), so without this a hand-typed
 * link would be a second cache entry for the same data — and a row compared
 * against it would never light.
 */
const upperCodeSchema = codeParamSchema.transform((code) => code.toUpperCase())

/** A sector CODE (`BANKING`), never its display name. Absent means the default sector. */
export const sectorParamSchema = upperCodeSchema.optional().catch(undefined)

/** A fund symbol (`NIBLSF`) — e.g. the fund whose sector dialog is open. */
export const fundParamSchema = upperCodeSchema.optional().catch(undefined)

/**
 * The trends chart's funds: 0 to `MAX_TREND_FUNDS` distinct symbols.
 *
 * Accepts a real array (what the router's JSON codec round-trips once the app
 * itself has navigated) or a comma string (`?funds=NIBLSF,NFCF`, what a
 * hand-typed or pasted link looks like) and always produces an array — the
 * shape `compareCodesSchema` (`screens/brokers/search.ts`) settled on for the
 * same reason. Over the limit keeps the first six rather than rejecting the
 * link. An EMPTY list is a real selection — the member removed every chip, and
 * the chart says to add a fund (the mock's) — while an absent or unreadable
 * one falls back to the default selection (`undefined`).
 */
export const trendFundsParamSchema = z
  .union([z.array(z.string()), z.string().transform((raw) => raw.split(','))])
  .transform((codes) =>
    [
      ...new Set(codes.map((code) => code.trim().toUpperCase()).filter((code) => code !== '')),
    ].slice(0, MAX_TREND_FUNDS),
  )
  .pipe(z.array(z.string().min(1)))
  .optional()
  .catch(undefined)

/**
 * Client-side paging over a list already in hand — Fund rankings, the Monthly
 * holdings table. The app's paging contract (`schemas/table.ts`), with 20 as
 * the opening size: the broker family's default for a ranked list, and what
 * shows a 56-fund ranking in three pages.
 */
export const pagingParams = {
  page: z.number().int().min(1).default(1).catch(1),
  pageSize: pageSizeSchema.default(20).catch(20),
}
